import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import StatCard from "@/components/StatCard";
import { fmtIDR, fmtShortIDR, firstOfMonthISO, todayISO } from "@/lib/format";
import {
  Wallet,
  Users as UsersIcon,
  UserPlus,
  Repeat,
  Activity,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Stethoscope,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarDays } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

const SEVERITY = {
  high: { bg: "bg-rose-50", text: "text-rose-700", icon: AlertTriangle },
  medium: { bg: "bg-amber-50", text: "text-amber-700", icon: AlertTriangle },
  low: { bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2 },
};

export default function Dashboard() {
  const { branchId } = useBranch();
  const [metrics, setMetrics] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topT, setTopT] = useState([]);
  const [topD, setTopD] = useState([]);
  const [struggle, setStruggle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === "month") {
      setDateFrom(firstOfMonthISO());
      setDateTo(todayISO());
    } else if (preset === "last_month") {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      setDateFrom(first.toISOString().slice(0, 10));
      setDateTo(last.toISOString().slice(0, 10));
    } else if (preset === "7d" || preset === "30d") {
      const d = new Date(now);
      d.setDate(d.getDate() - (preset === "7d" ? 6 : 29));
      setDateFrom(d.toISOString().slice(0, 10));
      setDateTo(todayISO());
    }
  };

  useEffect(() => {
    setLoading(true);
    const params = { branch_id: branchId, date_from: dateFrom, date_to: dateTo };
    Promise.all([
      api.get("/dashboard/sales-metrics", { params }),
      api.get("/dashboard/revenue-trend", { params }),
      api.get("/dashboard/top-treatments", { params }),
      api.get("/dashboard/top-doctors", { params }),
      api.get("/dashboard/struggle-analysis", { params: { branch_id: branchId } }),
    ])
      .then(([m, t, tt, td, sa]) => {
        setMetrics(m.data);
        setTrend(t.data);
        setTopT(tt.data);
        setTopD(td.data);
        setStruggle(sa.data);
      })
      .finally(() => setLoading(false));
  }, [branchId, dateFrom, dateTo]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Dashboard Sales</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Omzet & operasional clinic untuk periode {dateFrom} s/d {dateTo}.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2" data-testid="dashboard-date-filter">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Dari</label>
            <Input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[150px]" data-testid="dashboard-date-from" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Sampai</label>
            <Input type="date" value={dateTo} min={dateFrom} max={todayISO()} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[150px]" data-testid="dashboard-date-to" />
          </div>
          <div className="flex gap-1">
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => applyPreset("month")} data-testid="preset-month"><CalendarDays className="mr-1 h-3.5 w-3.5" />Bulan Ini</Button>
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => applyPreset("last_month")} data-testid="preset-last-month">Bulan Lalu</Button>
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => applyPreset("7d")} data-testid="preset-7d">7 Hari</Button>
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => applyPreset("30d")} data-testid="preset-30d">30 Hari</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testId="stat-revenue"
          label="Total Revenue (Periode)"
          value={metrics ? fmtShortIDR(metrics.total_revenue) : "—"}
          sublabel={metrics ? `${metrics.tx_count} transaksi` : ""}
          icon={Wallet}
          accent="emerald"
        />
        <StatCard
          testId="stat-new-patients"
          label="Pasien Baru"
          value={metrics ? metrics.new_patients : "—"}
          sublabel="Realtime bulan ini"
          icon={UserPlus}
          accent="indigo"
        />
        <StatCard
          testId="stat-existing-patients"
          label="Pasien Existing"
          value={metrics ? metrics.existing_patients : "—"}
          sublabel="Bertransaksi bulan ini"
          icon={UsersIcon}
          accent="sky"
        />
        <StatCard
          testId="stat-visit-tx"
          label="Visit → Transaksi"
          value={metrics ? `${metrics.visit_to_tx_pct}%` : "—"}
          sublabel={metrics ? `${metrics.finished_visits} visit selesai` : ""}
          icon={Repeat}
          accent="amber"
        />
        <StatCard
          testId="stat-aov-clinic"
          label="AOV Clinic"
          value={metrics ? fmtShortIDR(metrics.aov_clinic) : "—"}
          icon={Activity}
        />
        <StatCard
          testId="stat-aov-doctor"
          label="AOV Dokter (avg)"
          value={metrics ? fmtShortIDR(metrics.aov_doctor_avg) : "—"}
          icon={Stethoscope}
        />
        <StatCard
          testId="stat-scheduled"
          label="Booking Scheduled"
          value={metrics ? metrics.scheduled_count : "—"}
          icon={TrendingUp}
          accent="sky"
        />
        <StatCard
          testId="stat-cancel"
          label="No-Show / Cancel"
          value={metrics ? metrics.cancel_count : "—"}
          icon={AlertTriangle}
          accent="rose"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Trend Revenue 30 Hari</CardTitle>
            <CardDescription>Performa harian, pantau lonjakan & penurunan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(173 58% 39%)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(173 58% 39%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                  <Tooltip formatter={(v) => fmtIDR(v)} labelFormatter={(l) => `Tanggal: ${l}`} />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(173 58% 39%)" fill="url(#rev)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="struggle-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Analisa Struggle</CardTitle>
            <CardDescription>Indikator yang butuh perhatian</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {struggle?.issues?.map((it, idx) => {
              const s = SEVERITY[it.severity] || SEVERITY.low;
              const Icon = s.icon;
              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 rounded-md px-3 py-2.5 ${s.bg} ${s.text}`}
                >
                  <Icon className="mt-0.5 h-4 w-4" />
                  <div className="text-sm">
                    <div className="font-medium">{it.area}</div>
                    <div className="opacity-80">{it.note}</div>
                  </div>
                </div>
              );
            })}
            {struggle?.benchmarks && (
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-md border border-border p-2">
                  <div className="text-muted-foreground">Traffic Δ</div>
                  <div className="font-medium">{struggle.benchmarks.traffic_delta_pct}%</div>
                </div>
                <div className="rounded-md border border-border p-2">
                  <div className="text-muted-foreground">Closing Rate</div>
                  <div className="font-medium">{struggle.benchmarks.closing_rate_pct}%</div>
                </div>
                <div className="rounded-md border border-border p-2">
                  <div className="text-muted-foreground">MPP Dokter</div>
                  <div className="font-medium">{struggle.benchmarks.doctor_count}</div>
                </div>
                <div className="rounded-md border border-border p-2">
                  <div className="text-muted-foreground">MPP Therapist</div>
                  <div className="font-medium">{struggle.benchmarks.therapist_count}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Top Treatment Sales</CardTitle>
            <CardDescription>Treatment dengan revenue tertinggi</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topT.slice(0, 7)} layout="vertical" margin={{ left: 8, right: 8 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={(v) => fmtIDR(v)} />
                  <Bar dataKey="total" fill="hsl(271 76% 53%)" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Top Ranked Doctors</CardTitle>
            <CardDescription>Dokter dengan kontribusi sales terbesar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topD.slice(0, 6).map((d, idx) => (
                <div
                  key={d.doctor_id}
                  data-testid={`top-doctor-${idx}`}
                  className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
                >
                  <div className="grid h-8 w-8 place-items-center rounded-full bg-secondary text-xs font-medium">
                    {idx + 1}
                  </div>
                  {d.avatar_url ? (
                    <img src={d.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-secondary" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium">{d.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {d.specialty} • {d.unique_patients} pasien
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{fmtShortIDR(d.total)}</div>
                    <div className="text-[10px] text-muted-foreground">
                      AOV {fmtShortIDR(d.aov)}
                    </div>
                  </div>
                </div>
              ))}
              {!loading && topD.length === 0 && (
                <div className="text-sm text-muted-foreground">Belum ada transaksi.</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
